import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { CalendarClock, Plus, Pencil, Trash2, CheckCircle2, AlertTriangle, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { group: 'Truck & Equipment', options: [
    { v: 'truck_loan_payment', label: 'Truck loan / payment' },
    { v: 'insurance_payment', label: 'Insurance payment' },
    { v: 'insurance_renewal', label: 'Insurance renewal' },
    { v: 'registration_renewal', label: 'Registration renewal' },
    { v: 'tags_plates', label: 'Tags / plates' },
    { v: 'maintenance_schedule', label: 'Maintenance schedule' },
    { v: 'warranty_expiration', label: 'Warranty expiration' },
    { v: 'lease_payment', label: 'Lease payment' },
  ]},
  { group: 'Tax & Regulatory', options: [
    { v: 'form_2290', label: 'Form 2290' },
    { v: 'ifta_filing', label: 'IFTA filing' },
    { v: 'irp_renewal', label: 'IRP renewal' },
    { v: 'dot_inspection', label: 'DOT inspection' },
    { v: 'fmcsa_deadline', label: 'FMCSA deadline' },
    { v: 'ucr_renewal', label: 'UCR renewal' },
    { v: 'state_permit', label: 'State permit' },
  ]},
  { group: 'Driver', options: [
    { v: 'driver_training_expiration', label: 'Driver training expiration' },
    { v: 'mvr_review', label: 'MVR review' },
    { v: 'other', label: 'Other' },
  ]},
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.flatMap(g => g.options.map(o => [o.v, o.label])));

const TARGET_KINDS = [
  { v: 'company', label: 'Company' },
  { v: 'truck', label: 'Truck' },
  { v: 'trailer', label: 'Trailer' },
  { v: 'driver', label: 'Driver' },
  { v: 'employee', label: 'Employee' },
];

const emptyForm = {
  title: '', category: 'form_2290', due_date: '', target_kind: 'company',
  target_label: '', recurring_days: '', notes: '', amount: '',
};

const statusPill = (days, status) => {
  if (status === 'completed') return { cls: 'bg-emerald-50 text-emerald-700', label: 'Completed' };
  if (status === 'cancelled') return { cls: 'bg-slate-100 text-slate-500', label: 'Cancelled' };
  if (days == null) return { cls: 'bg-slate-100 text-slate-600', label: '—' };
  if (days < 0) return { cls: 'bg-red-100 text-red-700', label: `Overdue ${-days}d` };
  if (days === 0) return { cls: 'bg-amber-100 text-amber-800', label: 'Due today' };
  if (days <= 7) return { cls: 'bg-amber-50 text-amber-700', label: `${days}d left` };
  if (days <= 30) return { cls: 'bg-blue-50 text-[#002FA7]', label: `${days}d left` };
  return { cls: 'bg-slate-100 text-slate-600', label: `${days}d left` };
};

const ItemModal = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || emptyForm);
  const [saving, setSaving] = useState(false);
  const isEdit = !!(initial && initial.id);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title, category: form.category, due_date: form.due_date,
        target_kind: form.target_kind, target_label: form.target_label || null,
        recurring_days: form.recurring_days ? parseInt(form.recurring_days, 10) : null,
        notes: form.notes || null,
        amount: form.amount ? parseFloat(form.amount) : null,
      };
      if (isEdit) {
        await axios.put(`${API}/compliance-items/${initial.id}`, payload, { withCredentials: true });
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/compliance-items`, payload, { withCredentials: true });
        toast.success('Item created');
      }
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="compliance-modal">
      <div className="bg-white rounded-md shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {isEdit ? 'Edit compliance item' : 'New compliance item'}
          </h3>
          <button onClick={onClose} data-testid="compliance-modal-close" className="p-1 rounded hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Title</label>
            <input required value={form.title} onChange={set('title')} data-testid="compliance-title"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Category</label>
              <select value={form.category} onChange={set('category')} data-testid="compliance-category"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none">
                {CATEGORIES.map((g) => (
                  <optgroup key={g.group} label={g.group}>
                    {g.options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Due date</label>
              <input type="date" required value={form.due_date} onChange={set('due_date')} data-testid="compliance-due-date"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Applies to</label>
              <select value={form.target_kind} onChange={set('target_kind')} data-testid="compliance-target-kind"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none">
                {TARGET_KINDS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Label (e.g., truck #202)</label>
              <input value={form.target_label} onChange={set('target_label')} data-testid="compliance-target-label"
                placeholder="Optional"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Amount ($)</label>
              <input type="number" step="0.01" value={form.amount} onChange={set('amount')} data-testid="compliance-amount"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Repeats every (days)</label>
              <input type="number" min="1" value={form.recurring_days} onChange={set('recurring_days')} data-testid="compliance-recurring"
                placeholder="e.g. 365"
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Notes</label>
            <textarea rows={3} value={form.notes} onChange={set('notes')} data-testid="compliance-notes"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
            <button type="submit" disabled={saving} data-testid="compliance-save"
              className="px-4 py-2 bg-[#002FA7] text-white text-sm font-semibold rounded-md hover:bg-[#00247D] disabled:opacity-50 flex items-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {isEdit ? 'Save changes' : 'Create item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ComplianceCenter = () => {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [it, sm] = await Promise.all([
        axios.get(`${API}/compliance-items${statusFilter === 'all' ? '' : `?status=${statusFilter}`}`, { withCredentials: true }),
        axios.get(`${API}/compliance-items/summary`, { withCredentials: true }),
      ]);
      setItems(it.data); setSummary(sm.data);
    } catch (err) {
      toast.error('Could not load compliance items');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const overdue = useMemo(() => items.filter(i => i.status === 'active' && (i.days_remaining ?? 0) < 0), [items]);

  const complete = async (item) => {
    try {
      await axios.put(`${API}/compliance-items/${item.id}`, { status: 'completed' }, { withCredentials: true });
      toast.success('Marked complete');
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };
  const removeItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/compliance-items/${item.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="compliance-center-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <div className="flex items-center gap-2">
              <CalendarClock className="w-6 h-6 text-[#002FA7]" />
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Compliance Center
              </h1>
            </div>
            <p className="text-sm text-slate-600 mt-1">Track every due date across trucks, taxes, and drivers.</p>
          </div>
          <button data-testid="compliance-new-btn" onClick={() => setModal({})}
            className="inline-flex items-center gap-2 bg-[#002FA7] text-white text-sm font-semibold rounded-md px-4 py-2 hover:bg-[#00247D]">
            <Plus className="w-4 h-4" /> New item
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-4"><p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Active</p><p data-testid="summary-active" className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{summary.active || 0}</p></div>
          <div className="bg-white border border-red-200 rounded-md p-4"><p className="text-xs font-semibold uppercase tracking-widest text-red-600">Overdue</p><p data-testid="summary-overdue" className="text-2xl font-bold text-red-700 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{summary.overdue || 0}</p></div>
          <div className="bg-white border border-amber-200 rounded-md p-4"><p className="text-xs font-semibold uppercase tracking-widest text-amber-700">Due ≤ 30d</p><p data-testid="summary-upcoming" className="text-2xl font-bold text-amber-800 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{summary.upcoming_30d || 0}</p></div>
          <div className="bg-white border border-slate-200 rounded-md p-4"><p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Completed</p><p data-testid="summary-completed" className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{summary.completed || 0}</p></div>
        </div>

        {overdue.length > 0 && statusFilter === 'active' && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4 flex items-center gap-2" data-testid="overdue-banner">
            <AlertTriangle className="w-4 h-4 text-red-600" />
            <span className="text-sm text-red-900"><strong>{overdue.length}</strong> overdue item{overdue.length === 1 ? '' : 's'} need attention.</span>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex gap-1 flex-wrap">
            {['active', 'completed', 'cancelled', 'all'].map((s) => (
              <button key={s} data-testid={`filter-${s}`} onClick={() => setStatusFilter(s)}
                className={`text-xs font-semibold px-2.5 py-1.5 rounded ${statusFilter === s ? 'bg-[#002FA7] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          {loading ? (
            <div className="p-10 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="compliance-empty">No items in this view.</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Title</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Category</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Applies to</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Due</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-widest text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const pill = statusPill(it.days_remaining, it.status);
                  return (
                    <tr key={it.id} data-testid={`compliance-row-${it.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{it.title}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{CATEGORY_LABEL[it.category] || it.category}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{it.target_label || it.target_kind}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{it.due_date?.slice(0, 10)}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-semibold ${pill.cls}`}>{pill.label}</span></td>
                      <td className="px-4 py-3 text-right space-x-3 whitespace-nowrap">
                        {it.status === 'active' && (
                          <button onClick={() => complete(it)} data-testid={`complete-${it.id}`} className="text-emerald-600 hover:text-emerald-800" title="Mark complete">
                            <CheckCircle2 className="w-4 h-4 inline" />
                          </button>
                        )}
                        <button onClick={() => setModal(it)} data-testid={`edit-${it.id}`} className="text-[#002FA7] hover:text-[#00247D]" title="Edit">
                          <Pencil className="w-4 h-4 inline" />
                        </button>
                        <button onClick={() => removeItem(it)} data-testid={`delete-${it.id}`} className="text-red-600 hover:text-red-800" title="Delete">
                          <Trash2 className="w-4 h-4 inline" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {modal !== null && (
          <ItemModal
            initial={modal.id ? {
              ...modal,
              title: modal.title || '',
              category: modal.category || 'form_2290',
              due_date: modal.due_date?.slice(0, 10) || '',
              target_kind: modal.target_kind || 'company',
              target_label: modal.target_label || '',
              notes: modal.notes || '',
              recurring_days: modal.recurring_days ?? '',
              amount: modal.amount ?? '',
            } : emptyForm}
            onClose={() => setModal(null)}
            onSaved={fetchAll}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default ComplianceCenter;
